import { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { ProfileLink } from "@/components/ProfileDrawer";
import { formatNaira, formatDateDDMMYYYY } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Send, X } from "lucide-react";

const newLineItem = () => ({ id: crypto.randomUUID(), description: "", qty: 0, rate: 0, vat: true });
const seedLineItem = () => ({ id: crypto.randomUUID(), description: "Day shift guard hours", qty: 240, rate: 1500, vat: true });

const renderInvoiceAction = (i, onSend) => {
  if (i.status !== "DRAFT") return null;
  return (
    <button onClick={() => onSend(i.id)} className="btn-outline" style={{ padding: "6px 12px", fontSize: 12 }}>
      <Send size={12} /> Send
    </button>
  );
};

export const InvoicesAdmin = () => {
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    client_id: "", period_start: "", period_end: "", due_at: "",
    line_items: [{ description: "Day shift guard hours", qty: 240, rate: 1500, vat: true }],
  });

  const load = useCallback(async () => {
    const r = await api.get("/invoices");
    setInvoices(r.data);
  }, []);

  useEffect(() => {
    load();
    api.get("/clients").then(r => setClients(r.data));
  }, [load]);

  const clientName = (id) => clients.find(c => c.id === id)?.name || "—";

  const send = async (id) => {
    await api.put(`/invoices/${id}/send`);
    toast.success("Invoice sent to client");
    load();
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/invoices", {
        ...form,
        period_start: new Date(form.period_start).toISOString(),
        period_end: new Date(form.period_end).toISOString(),
        due_at: new Date(form.due_at).toISOString(),
      });
      toast.success("Invoice created");
      setOpen(false);
      load();
    } catch { toast.error("Failed"); }
  };

  const updateLine = (idx, key, val) => {
    const li = [...form.line_items];
    const next = { ...li[idx], [key]: key === "vat" ? val : (key === "description" ? val : Number(val)) };
    li[idx] = next;
    setForm({ ...form, line_items: li });
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-muted" style={{ fontSize: 14 }}>{invoices.length} invoices</div>
        <button className="btn-primary inline-flex items-center gap-2" onClick={() => setOpen(true)} data-testid="add-invoice">
          <Plus size={14} /> New invoice
        </button>
      </div>

      <div className="s-card" style={{ padding: 0 }}>
        <table className="s-table">
          <thead><tr><th>Invoice #</th><th>Client</th><th>Total</th><th>Due</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {invoices.map(i => (
              <tr key={i.id} data-testid={`invoice-${i.id}`}>
                <td className="mono">{i.invoice_no}</td>
                <td><ProfileLink kind="client" id={i.client_id}>{clientName(i.client_id)}</ProfileLink></td>
                <td className="mono text-teal">{formatNaira(i.totals?.total || 0)}</td>
                <td className="mono">{formatDateDDMMYYYY(i.due_at)}</td>
                <td><StatusBadge status={i.status} /></td>
                <td>
                  {renderInvoiceAction(i, send)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div onClick={() => setOpen(false)} style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex",
          alignItems: "center", justifyContent: "center", zIndex: 100,
        }}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="s-card" style={{ width: 560, maxWidth: "90vw", maxHeight: "90vh", overflowY: "auto" }}>
            <div className="flex justify-between items-center mb-4">
              <div className="h-section">New invoice</div>
              <button type="button" onClick={() => setOpen(false)} className="text-muted"><X size={18} /></button>
            </div>
            <label className="text-muted" style={{ fontSize: 12 }}>Client</label>
            <select required value={form.client_id} onChange={(e) => setForm({...form, client_id: e.target.value})} className="s-input mt-1 mb-3">
              <option value="">Select...</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-muted" style={{ fontSize: 12 }}>Period start</label>
                <input type="date" required value={form.period_start} onChange={(e) => setForm({...form, period_start: e.target.value})} className="s-input mt-1" />
              </div>
              <div>
                <label className="text-muted" style={{ fontSize: 12 }}>Period end</label>
                <input type="date" required value={form.period_end} onChange={(e) => setForm({...form, period_end: e.target.value})} className="s-input mt-1" />
              </div>
            </div>
            <label className="text-muted mt-3 block" style={{ fontSize: 12 }}>Due date</label>
            <input type="date" required value={form.due_at} onChange={(e) => setForm({...form, due_at: e.target.value})} className="s-input mt-1 mb-3" />

            <div className="h-section mt-3 mb-2" style={{ fontSize: 14 }}>Line items</div>
            {form.line_items.map((li, idx) => (
              <div key={li.id} className="grid grid-cols-12 gap-2 mb-2">
                <input className="s-input col-span-5" placeholder="Description" value={li.description} onChange={(e) => updateLine(idx, "description", e.target.value)} />
                <input className="s-input col-span-2 mono" type="number" value={li.qty} onChange={(e) => updateLine(idx, "qty", e.target.value)} />
                <input className="s-input col-span-3 mono" type="number" value={li.rate} onChange={(e) => updateLine(idx, "rate", e.target.value)} />
                <label className="col-span-2 flex items-center gap-2 text-muted" style={{ fontSize: 12 }}>
                  <input type="checkbox" checked={li.vat} onChange={(e) => updateLine(idx, "vat", e.target.checked)} /> VAT
                </label>
              </div>
            ))}
            <button type="button" onClick={() => setForm({...form, line_items: [...form.line_items, newLineItem()]})} className="btn-outline mb-4" style={{ padding: "6px 12px", fontSize: 12 }}>
              + Add line
            </button>
            <div className="text-muted" style={{ fontSize: 12, marginBottom: 12 }}>
              Subtotal: {formatNaira(lineTotals.subtotal)} ·
              VAT 7.5%: {formatNaira(lineTotals.vat)}
            </div>
            <button type="submit" className="btn-primary w-full">Create invoice</button>
          </form>
        </div>
      )}
    </div>
  );
};
